import { useState } from "react";
import { useErrors } from "../../contexts/ErrorContext";

/**
 * Floating toast panel showing recent app errors with expandable stack traces.
 * Only renders for users holding the `view_error_details` role.
 *
 * Ported from Captain's Log.
 */
export function ErrorToast() {
  const { errors, dismissError, clearAll, canViewErrors } = useErrors();
  const [expandedErrors, setExpandedErrors] = useState<Set<number>>(new Set());

  if (!canViewErrors || errors.length === 0) {
    return null;
  }

  const toggleExpanded = (errorId: number) => {
    setExpandedErrors((prev) => {
      const next = new Set(prev);
      if (next.has(errorId)) {
        next.delete(errorId);
      } else {
        next.add(errorId);
      }
      return next;
    });
  };

  const formatTime = (date: Date) =>
    date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });

  return (
    <div className="error-toast-container">
      <div className="error-toast-header">
        <span className="error-toast-title">Error Details ({errors.length})</span>
        {errors.length > 1 && (
          <button onClick={clearAll} className="error-toast-clear-all">
            Clear All
          </button>
        )}
      </div>

      <div className="error-toast-list">
        {errors.map((error) => (
          <div key={error.id} className="error-toast-item">
            <div className="error-toast-item-header">
              <div className="error-toast-info">
                <span className="error-toast-time">{formatTime(error.timestamp)}</span>
                <span className="error-toast-code">{error.code}</span>
              </div>
              <button
                onClick={() => dismissError(error.id)}
                className="error-toast-dismiss"
                title="Dismiss"
                type="button"
              >
                ×
              </button>
            </div>

            <div className="error-toast-message">{error.message}</div>

            {error.stack && (
              <div className="error-toast-details">
                <button
                  type="button"
                  className="error-toast-stack-toggle"
                  onClick={() => toggleExpanded(error.id)}
                >
                  {expandedErrors.has(error.id) ? "Hide" : "Show"} stack trace
                </button>
                {expandedErrors.has(error.id) && (
                  <pre className="error-toast-stack">{error.stack}</pre>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
